// STT via Deepgram, proxied through backend WebSocket at /ws/stt/stream.
// Public API kept identical to the previous webkitSpeechRecognition wrapper,
// so callers (Lesson.tsx) need no changes.

interface STTOptions {
  lang: string;
  onResult: (text: string, isFinal: boolean) => void;
  onError?: (err: string) => void;
  onEnd?: () => void;
}

type ServerMsg =
  | { type: "open" }
  | { type: "close" }
  | { type: "error"; error: string }
  | { type: "utteranceEnd" }
  | { type: "transcript"; text: string; isFinal: boolean; speechFinal: boolean };

export class STT {
  private ws: WebSocket | null = null;
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private listening = false;
  private committed = "";
  private gotAnyText = false;

  constructor(private opts: STTOptions) {}

  static isSupported(): boolean {
    if (typeof window === "undefined") return false;
    return (
      typeof WebSocket !== "undefined" &&
      typeof MediaRecorder !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia
    );
  }

  async start(): Promise<void> {
    if (this.listening) return;
    this.listening = true;
    this.committed = "";
    this.gotAnyText = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      this.listening = false;
      this.opts.onError?.(`mic-denied: ${String((e as Error)?.message ?? e)}`);
      this.opts.onEnd?.();
      return;
    }
    this.stream = stream;

    const wsProto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${wsProto}://${location.host}/ws/stt/stream?lang=${encodeURIComponent(this.opts.lang)}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      this.recorder = rec;
      rec.ondataavailable = (ev) => {
        if (ev.data.size === 0) return;
        if (ws.readyState !== WebSocket.OPEN) return;
        ev.data.arrayBuffer().then((ab) => {
          if (ws.readyState === WebSocket.OPEN) ws.send(ab);
        }).catch(() => { /* noop */ });
      };
      rec.onerror = (ev) => {
        this.opts.onError?.(`recorder: ${String((ev as any)?.error?.message ?? ev)}`);
      };
      rec.start(250);
    };

    ws.onmessage = (ev) => {
      let msg: ServerMsg;
      try { msg = JSON.parse(ev.data as string) as ServerMsg; } catch { return; }

      if (msg.type === "transcript") {
        this.gotAnyText = true;
        const incoming = msg.text.trim();
        if (msg.speechFinal) {
          const full = (this.committed + " " + incoming).trim();
          this.committed = "";
          this.opts.onResult(full, true);
          this.cleanup();
          this.opts.onEnd?.();
        } else if (msg.isFinal) {
          this.committed = (this.committed + " " + incoming).trim();
          this.opts.onResult(this.committed, false);
        } else {
          const combined = (this.committed + " " + incoming).trim();
          this.opts.onResult(combined, false);
        }
      } else if (msg.type === "utteranceEnd") {
        if (!this.gotAnyText) {
          this.opts.onError?.("no-speech");
          this.cleanup();
        } else if (this.committed) {
          // flush whatever we have as a final
          const full = this.committed;
          this.committed = "";
          this.opts.onResult(full, true);
          this.cleanup();
          this.opts.onEnd?.();
        }
      } else if (msg.type === "error") {
        this.opts.onError?.(msg.error || "unknown");
        this.cleanup();
      }
    };

    ws.onerror = () => {
      this.opts.onError?.("network");
      this.cleanup();
    };

    ws.onclose = () => {
      if (this.listening) {
        this.cleanup();
        this.opts.onEnd?.();
      }
    };
  }

  stop(): void {
    // gracefully request close — mirrors Web Speech .stop()
    try { this.recorder?.stop(); } catch { /* noop */ }
    try {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "finish" }));
      }
    } catch { /* noop */ }
  }

  abort(): void {
    this.cleanup();
  }

  get isListening(): boolean {
    return this.listening;
  }

  private cleanup(): void {
    this.listening = false;
    try { this.recorder?.stop(); } catch { /* noop */ }
    this.recorder = null;
    try { this.stream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    this.stream = null;
    try { this.ws?.close(); } catch { /* noop */ }
    this.ws = null;
  }
}
